import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import type { ExtractionWarning, PnDetailPrintLeg } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';

/**
 * Chips for sanitiser records from the latest extraction run (DR-2808) —
 * a field stripped from every row, whole rows dropped (DR-3017), a
 * chronology-conflict date (DR-3068), rows excluded for carrying aircraft
 * counters (DR-4861), a P/N Detail Print block that could not be read while the
 * page's other blocks were kept (DR-4947), or extra AD Rev values that were dropped
 * (DR-5981). Informational only — there is no
 * confirm/restore action; the reviewer checks the source page themselves. Unknown
 * future `code` / `rule` values fall back to generic copy so a new backend rule can
 * never blank the view.
 */

function rowsLabel(count: number) {
  return `${count} ${count === 1 ? 'row' : 'rows'}`;
}

const LEG_LABEL: Record<PnDetailPrintLeg, string> = {
  llp: 'LLP',
  ad: 'AD',
  sb: 'SB',
  qec: 'QEC',
  blade: 'blade',
  ldnd: 'LDND',
};

// DR-3017: `ExtractionWarning` is a union discriminated on `code`, and only the
// constant-column members carry `field` / `removedValue` — so the code switch has to come
// first, before any member-specific field is read. DR-4947: the same holds for `rowCount`,
// which the `leg-failed` member does not carry.
function describe(warning: ExtractionWarning): { label: string; explanation: string } {
  switch (warning.code) {
    case 'constant-column-stripped': {
      const rows = rowsLabel(warning.rowCount);
      // Read off before the inner switch: exhausting `rule` narrows `warning` itself to `never`,
      // so the default branch could no longer reach either field.
      const field = warning.field.toUpperCase();
      const removed = warning.removedValue;
      switch (warning.rule) {
        case 'llp-iin-equals-esn':
          return {
            label: `Removed ${field} matching the engine serial "${removed}" from ${rows}`,
            explanation: `Every row repeated the engine serial number as its ${field}, so the column was read as a document number rather than a part position and removed.`,
          };
        case 'llp-iin-constant-column':
          return {
            label: `Removed repeated ${field} "${removed}" from ${rows}`,
            explanation: `Every row printed the same ${field}, so the column was read as a document number rather than a part position and removed.`,
          };
        default:
          return {
            label: `Removed ${field} "${removed}" from ${rows}`,
            explanation: `The same ${field} value repeated across every row, so it was read as a document number rather than a per-row value and removed.`,
          };
      }
    }
    case 'incomplete-rows-dropped': {
      const rows = rowsLabel(warning.rowCount);
      return {
        // A row is dropped when EITHER value failed to parse — keeping it requires both — so the
        // copy says "or", not "and". "No readable TSN/CSN" would claim both were missing.
        label: `Dropped ${rows} missing a readable TSN or CSN`,
        explanation:
          'These rows had no readable time-since-new or cycles-since-new value, so they could not be published — check the source page.',
      };
    }
    case 'chronology-conflict':
      return {
        label: `Confirm ${rowsLabel(warning.rowCount)} with a date conflict`,
        explanation: 'An installation date sits before a removal at the same TSN and CSN.',
      };
    case 'aircraft-counter-rows-dropped':
      return {
        label: `Excluded ${rowsLabel(warning.rowCount)}: counters are aircraft hours and cycles`,
        explanation:
          "This page's only hours and cycles columns belong to the aircraft, not the engine, so its events were excluded instead of being published with airframe values as engine TSN and CSN — use a document with engine counters.",
      };
    case 'leg-failed': {
      const block = `${LEG_LABEL[warning.leg] ?? warning.leg} block`;
      return {
        label:
          warning.attempts > 1
            ? `Could not read the ${block} after ${warning.attempts} attempts`
            : `Could not read the ${block}`,
        explanation: `${warning.message.replace(/\.$/, '')}. The other blocks on this page were read. Re-extract the page to read this block again.`,
      };
    }
    case 'stacked-revision-extra-values':
      return {
        label: `AD ${warning.adNumber}: the Rev cell prints "${warning.cell}", more values than the row has AD numbers. Kept "${warning.kept}" and dropped the rest.`,
        explanation: 'The extra Rev values were read as a sheet error. Check the source page.',
      };
    default: {
      // An unknown future code: read `rowCount` only when the member carries it.
      const unknown = warning as { rowCount?: unknown };
      return {
        label:
          typeof unknown.rowCount === 'number'
            ? `Removed ${rowsLabel(unknown.rowCount)} during post-processing`
            : 'Removed data during post-processing',
        explanation: 'Post-processing removed data it judged to be misread — check the source page.',
      };
    }
  }
}

export function ExtractionWarnings({ warnings }: { warnings?: ExtractionWarning[] }) {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {warnings.map((warning, index) => {
        const { label, explanation } = describe(warning);
        return (
          <Badge
            key={`${warning.code}-${'rule' in warning ? warning.rule : 'leg' in warning ? warning.leg : warning.adNumber}-${index}`}
            color="amber"
            title={explanation}
          >
            <ExclamationTriangleIcon className="size-3.5 shrink-0" aria-hidden="true" />
            {label}
          </Badge>
        );
      })}
      <span className="text-xs text-gray-500 dark:text-zinc-400">
        Check the source page before relying on these rows.
      </span>
    </div>
  );
}
